import type { Category, QuickNote } from "../../domain/entities.ts";
import type { Root } from "../../domain/app-types.ts";
import { quickNoteTaskContent } from "./model.ts";
import type { QuickNoteConversionModel, QuickNoteDestination } from "./conversion-controller.ts";

export interface QuickNoteConversionWorkflowCapabilities {
  flush(): Promise<boolean>;
  activeNote(): QuickNote | undefined;
  roots(): Root[];
  categories(): Promise<Category[]>;
  activeRoot(): string | null;
  activeCategory(): string | null;
  personalRoot(): string | null;
  createTask(id: string, destination: QuickNoteDestination, content: { title: string; description: string }): Promise<void>;
  archiveNote(noteId: string, taskId: string): Promise<QuickNote>;
  replaceNote(note: QuickNote): void;
  orderedNotes(): QuickNote[];
  setActiveNote(id: string | null): void;
  renderNotes(): void;
  markLocal(): void;
  offerCreated(openTask: () => Promise<void>): void;
  closeNotes(): Promise<void>;
  selectDestination(destination: QuickNoteDestination): Promise<void>;
  openTask(id: string): Promise<void>;
  sync(): void;
  randomId(): string;
  icon(name: string): string;
  toast(message: string, error?: boolean): void;
  openDialog(model: QuickNoteConversionModel, convert: (destination: QuickNoteDestination) => Promise<boolean>): void;
}

export function createQuickNoteConversionWorkflow(capabilities: QuickNoteConversionWorkflowCapabilities) {
  return {
    async open(): Promise<void> {
      if (!await capabilities.flush()) return;
      const note = capabilities.activeNote();
      if (!note) return;
      const content = quickNoteTaskContent(note.body);
      if (!content.title) { capabilities.toast("Write something before converting this note", true); return; }
      const editableRoots = capabilities.roots().filter(root => root.role === "owner" || root.role === "editor");
      if (!editableRoots.length) { capabilities.toast("There is no editable task destination", true); return; }
      const categories = (await capabilities.categories()).filter(category => !category.deleted);
      capabilities.openDialog({
        title: content.title,
        description: content.description,
        roots: editableRoots,
        categories,
        activeRoot: capabilities.activeRoot(),
        activeCategory: capabilities.activeCategory(),
        icon: capabilities.icon,
      }, async destination => {
        const personalRoot = capabilities.personalRoot();
        if (!personalRoot) { capabilities.toast("Quick notes are not available for this account", true); return false; }
        const taskId = capabilities.randomId();
        await capabilities.createTask(taskId, destination, content);
        const archived = await capabilities.archiveNote(note.id, taskId);
        capabilities.replaceNote(archived);
        capabilities.setActiveNote(capabilities.orderedNotes()[0]?.id || null);
        capabilities.renderNotes();
        capabilities.markLocal();
        capabilities.offerCreated(async () => {
          await capabilities.closeNotes();
          await capabilities.selectDestination(destination);
          await capabilities.openTask(taskId);
        });
        capabilities.sync();
        return true;
      });
    },
  };
}
